import React from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet-active-area';

const CENTER_CHANGE_THRESHOLD = 0.005; //~500m

export class OsmMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMapCreated: false
    };
    this.map = null;
    this.listeners = [];
    this.handleMapLoad = this.handleMapLoad.bind(this);
    this.handleMapClick = this.handleMapClick.bind(this);
    this.handleMapMove = this.handleMapMove.bind(this);
    this.handleMapMoveEnd = this.handleMapMoveEnd.bind(this);
    this.handleMapZoom = this.handleMapZoom.bind(this);
    this.debouncedMapChanged = this.debounce(this.mapChanged, 300);
    this.debouncedMapMoveEnd = this.debounce(this.mapMoveEnd, 300);
  }

  componentDidMount() {
    this.createMap();
    this.resetMapCenter();
    this.resetMapCenterPhase2();
    this.setState({
      isMapCreated: true,
    });
    if (this.props.onMapCreated) {
      this.props.onMapCreated();
    }

    this.addListeners();
  }

  componentDidUpdate(prevProps) {
    const centerChanged = this.props.center !== prevProps.center;
    const zoomChanged = this.props.zoom !== prevProps.zoom;

    if (centerChanged || zoomChanged) {
      this.deferred = this.executeAndWaitForUpdate(() => {
        this.setCenterAndZoom();
      });
      this.deferred.promise.then(() => this.onMapUpdated());
    } else if (this.props.bounds && this.props.bounds !== prevProps.bounds) {
      this.deferred = this.executeAndWaitForUpdate(() => {
        this.fitBounds();
      });
      this.deferred.promise.then(() => this.onMapUpdated());
    }
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  setCenterAndZoom() {
    this.map.setView(this.props.center, this.props.zoom);
  }

  fitBounds() {
    let bounds = L.latLngBounds(this.props.bounds[0], this.props.bounds[1])
    this.map.fitBounds(bounds);
  }

  handleMapLoad() {
    //console.log(`MAP --tiles loaded--  zoom: ${this.map.getZoom()} center: ${JSON.stringify(this.map.getCenter())}`)
    this.debouncedMapChanged();
  }

  handleMapClick(event) {
    L.DomEvent.stopPropagation(event);

    if (this.props.onMapClicked) {
      this.props.onMapClicked(event);
    }
  }

  handleMapMove() {
    //console.log(`MAP --bounds changed--  zoom: ${this.map.getZoom()} center: ${JSON.stringify(this.map.getCenter())}`)
    this.debouncedMapChanged();
  }

  handleMapMoveEnd() {
    this.debouncedMapMoveEnd();
  }

  handleMapZoom() {
    //console.log(`MAP --zoom changed--  zoom: ${this.map.getZoom()} center: ${JSON.stringify(this.map.getCenter())}`)
    this.debouncedMapChanged();
    this.props.onZoomChanged(this.map.getZoom());
  }

  onMapUpdated() {
    this.deferred = null;
    const center = this.getCenter();
    const bounds = this.getBounds();
    if(center && bounds) {
      this.props.onMapUpdated({center: center, bounds: bounds});
    }
  }

  debounce(func, wait, immediate) {
    var timeout;
    return function() {
      var context = this, args = arguments;
      var later = function() {
        timeout = null;
        if (!immediate) { func.apply(context, args); }
      };
      var callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) { func.apply(context, args); }
    };
  }

  /*
    Using deferred prevents centerChanged or boundsChanged to be called when the map is in the
    updating phase.
    To avoid unnecessary store reloads when center is changed we need to add a threshold
    to decide it was only a 'bounds' change or a center change.
  */
  mapChanged() {
    if (this.deferred) {
      this.resetMapCenter();
      this.deferred.resolve();
    } else {
      let center = this.getCenter();
      let bounds = this.getBounds();
      let changeInCenter = this.changeInCenter(center);
      if (changeInCenter > CENTER_CHANGE_THRESHOLD) {
        this.resetMapCenter(center);
        if (this.props.onCenterChanged) {
          this.props.onCenterChanged({center: center, bounds: bounds});
        }
      } else {
        this.props.onBoundsChanged({bounds: bounds});
      }
    }
  }

  mapMoveEnd() {
    if (this.deferred) {
      this.resetMapCenterPhase2();
    } else {
      let center = this.getCenter();
      let bounds = this.getBounds();
      let changeInCenter = this.changeInCenterPhase2(center);
      if (changeInCenter > CENTER_CHANGE_THRESHOLD) {
        this.resetMapCenterPhase2(center);
        if (this.props.onMoveEnd) {
          this.props.onMoveEnd({center, bounds});
        }
      }
    }
  }

  resetMapCenter(center) {
    this.mapCenter = center || this.getCenter();
  }

  resetMapCenterPhase2(center) {
    this.mapCenterPhase2 = center || this.getCenter();
  }

  changeInCenter(center) {
    let mapCenter = this.mapCenter || center;
    let latDiff = this.diff(mapCenter.lat, center.lat);
    let lngDiff = this.diff(mapCenter.lng, center.lng);
    return Math.max(latDiff, lngDiff);
  }

  changeInCenterPhase2(center) {
    let mapCenter = this.mapCenterPhase2 || center;
    let latDiff = this.diff(mapCenter.lat, center.lat);
    let lngDiff = this.diff(mapCenter.lng, center.lng);
    return Math.max(latDiff, lngDiff);
  }

  diff(coordinate1, coordinate2) {
    return Math.abs(parseFloat(coordinate1.toFixed(5)) - parseFloat(coordinate2.toFixed(5)));
  }

  getCenter() {
    return this.map.getCenter();
  }

  getBounds() {
    let bounds = this.map.getBounds();
    return [bounds.getSouthWest(), bounds.getNorthEast()]
  }

  executeAndWaitForUpdate(func) {
    let deferred = {};
    deferred.promise = new Promise(function(resolve, reject) {
      deferred.resolve = resolve;
      deferred.reject = reject;
      func();
      setTimeout(function() {
        resolve();
      }, 5000);
    });
    return deferred;
  }

  addListeners() {
    let events = [
      ['load', this.handleMapLoad],
      ['click', this.handleMapClick],
      ['move', this.handleMapMove],
      ['moveend', this.handleMapMoveEnd],
      ['zoom', this.handleMapZoom],
    ];
    events.forEach(event => {
      this.map.on(event[0], event[1])
    });
  }

  removeListeners() {
    this.map.off();
  }

  getMaxBounds(corners) {
    const corner1 = L.latLng(corners[0][0], corners[0][1]),
          corner2 = L.latLng(corners[1][0], corners[1][1]);

    return L.latLngBounds(corner1, corner2);
  }

  createMap() {
    const maxBounds = this.getMaxBounds(this.props.maxBoundsCorners);
    this.map = L.map(this.node, {
      ...this.props,
      center: this.props.center,
      zoom: this.props.zoom,
      zoomControl: false,
      maxBounds: maxBounds,
      dragging: this.props.isPhase2Map ? true : !L.Browser.mobile,
      tap: !L.Browser.mobile
    }).setActiveArea('activeArea');
    L.tileLayer(this.props.osmUrl, {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18
    }).addTo(this.map);
    L.control.zoom({
      position: 'bottomright'
    }).addTo(this.map);
  }

  getChildren() {
    return React.Children.map(this.props.children, (child) => {
      if (!React.isValidElement(child)) {
        return child;
      }
      return React.cloneElement(child, {
        map: this.map
      });
    });
  }

  render() {
    const style = Object.assign({
      width: this.props.width,
      height: this.props.height
    }, this.props.style);

    return (
      <div style={style} className={this.props.className}
        ref={(node) => { this.node = node; }}>
        {this.state.isMapCreated ? this.getChildren() : null}
      </div>
    );
  }
}

Map.propTypes = {
  center: PropTypes.object,
  zoom: PropTypes.number,

  /* bounds of the map */
  bounds: PropTypes.object,

  /* Called after the center of the map changed triggered by the user */
  onCenterChanged: PropTypes.func,

  /* Called after the bounds of the map changed triggered by the user */
  onBoundsChanged: PropTypes.func,

  /* Called after the options of the map are set triggered by props changes */
  onMapUpdated: PropTypes.func,

  onZoomChanged: PropTypes.func
};
